import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, localStorageKeys, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateLoadNatureMutation } from '../../../../hooks/mutations/useLoadNatureMutation';
import { useLoadNaturesQuery } from '../../../../hooks/queries/useLoadNaturesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { LoadNature } from '../../../../services/apiSLTP/materials/loadNatures';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateLoadNatureModal from '../../../modals/admin/internal/UpdateLoadNatureModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface LoadNaturesSettingsRowProps {
    withUsageCount: boolean;
}

const LoadNaturesSettingsRow: React.FC<LoadNaturesSettingsRowProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLoadNature, setSelectedLoadNature] = useState<LoadNature | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();

    const {
        data: dataLoadNatures,
        isLoading: isLoadNaturesLoading,
        refetch: refetchLoadNatures,
    } = useLoadNaturesQuery({ queries: { withUsage: withUsageCount } });

    const {
        mutate: mutateLoadNature,
        isSuccess: isSuccessLoadNature,
        isError: isErrorLoadNature,
        isLoading: isLoadingLoadNature,
        error: errorLoadNature,
        data: dataLoadNature,
    } = useUpdateLoadNatureMutation({
        onSuccess: (data, variables) => {
            refetchLoadNatures();
        },
        onError: (error) => {
            console.error('Error updating loadNature:', error.message);
        },
    });

    const sortedLoadNatures = () => {
        return (dataLoadNatures || []).sort((a, b) => {
            const typeA = a?.designation;
            const typeB = b?.designation;
            if (typeA < typeB) return -1;
            if (typeA > typeB) return 1;
            const designationA = a.designation;
            const designationB = b.designation;
            if (designationA < designationB) return -1;
            if (designationA > designationB) return 1;
            return 0;
        });
    };

    const columnDefs: ColDef<LoadNature>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<LoadNature>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyLoadNature'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedLoadNature(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteLoadNature'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedLoadNature(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<LoadNature>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('wasteCode')),
            field: 'defaultWasteCodeId.designation',
            colId: 'wasteCode',
        },
        {
            headerName: capitalizeFirstLetter(t('displayOrder')),
            field: 'displayOrder',
            colId: 'displayOrder',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('createdBy')),
            valueGetter: (params) => formatName(params.data?.createdBy?.firstName, params.data?.createdBy?.lastName),
            colId: 'createdBy',
        },
        createDateTimeColumn<LoadNature>('createdAt', 'createdAt'),
        createDateTimeColumn<LoadNature>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
                initialWidth: 250,
                colId: '_id',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'total',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                field: 'usageCount.dailyTrucksPlanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xridesplanned',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRidesCompleted')),
                field: 'usageCount.dailyTrucksRides',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xridescompleted',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    return (
        <>
            <GenericAgGridTable
                rowData={sortedLoadNatures()}
                columnDefs={columnDefs}
                loading={isLoadNaturesLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedLoadNature(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addLoadNature'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.VEHICLES.LOADNATURES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateLoadNatureModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    loadNature={selectedLoadNature}
                    loadNatures={dataLoadNatures || []}
                    isEditMode={isEditMode}
                    refetch={() => refetchLoadNatures()}
                />
            )}

            {showDeleteModal && selectedLoadNature && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateLoadNature({ loadNatureId: selectedLoadNature._id, data: { crudStatus: crudStatusEnum.DELETED } });
                    }}
                    title={t('loadNature')}
                    itemName={selectedLoadNature.designation}
                />
            )}
        </>
    );
};

export default LoadNaturesSettingsRow;
